/**
 * RedirectPage Component - Handles short URL redirections
 * 
 * This component intercepts short URL requests and redirects to the backend
 * for proper redirect handling and analytics tracking.
 */

import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ExternalLink, AlertCircle, RotateCcw } from 'lucide-react';

export default function RedirectPage() {
  const { code } = useParams<{ code: string }>();
  const [status, setStatus] = useState<'loading' | 'redirecting' | 'error' | 'not-found'>('loading');
  const [errorMessage, setErrorMessage] = useState('');
  const [countdown, setCountdown] = useState(3);

  useEffect(() => {
    if (!code) {
      setStatus('error');
      setErrorMessage('Invalid URL code');
      return;
    }

    // Immediately redirect to backend for proper handling
    redirectToBackend(code);
  }, [code]);

  const redirectToBackend = async (shortCode: string) => {
    try {
      setStatus('redirecting');
      
      // 환경별 백엔드 URL 설정
      const backendUrl = import.meta.env.PROD 
        ? `https://shorty.kr/${shortCode}`  // 프로덕션: nginx가 처리
        : `http://localhost:5199/${shortCode}`;  // 개발: 직접 백엔드로
      
      console.log('Redirecting to:', backendUrl);
      
      // Start countdown
      const countdownInterval = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(countdownInterval);
            // Redirect to backend
            window.location.href = backendUrl;
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      // Also redirect immediately without waiting for countdown
      setTimeout(() => {
        window.location.href = backendUrl;
      }, 500);

    } catch (error) {
      console.error('Redirect error:', error);
      setStatus('error');
      setErrorMessage('Failed to process redirect');
    }
  };

  const handleManualRedirect = () => {
    if (code) {
      const backendUrl = import.meta.env.PROD 
        ? `https://shorty.kr/${code}`
        : `http://localhost:5199/${code}`;
      window.location.href = backendUrl;
    }
  };

  const renderContent = () => {
    switch (status) {
      case 'loading':
        return (
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Processing your request...
            </h1>
            <p className="text-gray-600 dark:text-gray-300">
              Analyzing the short URL: <span className="font-mono text-blue-600 dark:text-blue-400">{code}</span>
            </p>
          </div>
        );

      case 'redirecting':
        return (
          <div className="text-center">
            <div className="relative mb-6">
              <div className="animate-pulse">
                <ExternalLink className="h-16 w-16 text-blue-600 mx-auto" />
              </div>
              <div className="absolute -bottom-1 -right-1 bg-green-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">
                {countdown}
              </div>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Redirecting...
            </h1>
            <p className="text-gray-600 dark:text-gray-300 mb-4">
              You will be redirected in {countdown} second{countdown !== 1 ? 's' : ''}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
              Taking you to the destination safely
            </p>
            <button
              onClick={handleManualRedirect}
              className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors duration-200"
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              Redirect Now
            </button>
          </div>
        );

      case 'error':
        return (
          <div className="text-center">
            <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Redirect Error
            </h1>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              {errorMessage || 'An error occurred while processing the redirect'}
            </p>
            <div className="space-y-3">
              <button
                onClick={handleManualRedirect}
                className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors duration-200"
              >
                <RotateCcw className="h-4 w-4 mr-2" />
                Try Again
              </button>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                or <a href="/" className="text-blue-600 hover:text-blue-700 underline">go to homepage</a>
              </div>
            </div>
          </div>
        );

      case 'not-found':
        return (
          <div className="text-center">
            <div className="text-6xl mb-4">🔗</div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              URL Not Found
            </h1>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              The short URL <span className="font-mono text-red-500">{code}</span> does not exist or has been removed.
            </p>
            <a
              href="/"
              className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors duration-200"
            >
              Create New Short URL
            </a>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-md w-full bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8"
      >
        {renderContent()}
      </motion.div>
    </div>
  );
}
